/**
 * EngineBusy (P142) -- inline "still running, hasn't timed out" affordance
 * for a synchronous Engine call. Render it (only) while the call is
 * pending; it mounts the spinner + the action label + a ticking
 * elapsed-seconds counter ("Asking the Engine… 14s"). Because it's mounted
 * only while pending, its elapsed starts at the action and stops on
 * completion.
 *
 * `aria-live="polite"` so a screen reader announces the running state;
 * the spinner itself is aria-hidden.
 */
import { useTranslation } from "react-i18next";

import { Spinner } from "shared/components/Spinner";
import { useElapsedSeconds } from "shared/hooks/useElapsedSeconds";

interface EngineBusyProps {
  /** The action label, e.g. "Asking the Engine". */
  label: string;
  testid?: string;
}

export function EngineBusy({ label, testid = "engine-busy" }: EngineBusyProps) {
  const { t } = useTranslation();
  const seconds = useElapsedSeconds(true);
  return (
    <span
      data-testid={testid}
      aria-live="polite"
      className="inline-flex items-center gap-1.5"
    >
      <Spinner />
      <span>{label}</span>
      <span data-testid="engine-busy-elapsed" className="tabular-nums opacity-70">
        {t("common.elapsedSeconds", { count: seconds })}
      </span>
    </span>
  );
}
